import '@mantine/core/styles.css';
import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Alert, Anchor, Button, Checkbox, Group, PasswordInput, Stack, Text, TextInput, } from '@mantine/core';
import { CheckCircle, Info, WarningCircle } from '@phosphor-icons/react';
import { PublicContainer } from './PublicContainer';
import PublicAuthShell, { AuthCard } from '../Components/PublicAuthShell';
import { useAppContext } from '../Context/AppContext';
import classes from './Login.module.css';
// No server field carries this date, so it is maintained here.
const TERMS_UPDATED = 'May 7, 2026';
// WelcomeController hands these through ISN::getMessage()/getError(), which return
// false (not '') when unset, and omits redirect/rememberUserName/userName entirely
// in webview mode — hence the coercion rather than a plain ?? chain.
function readPageData(data) {
    const str = (v) => (typeof v === 'string' ? v : '');
    return {
        logoUrl: str(data?.logoUrl),
        message: str(data?.message),
        errorMessage: str(data?.errorMessage),
        redirect: str(data?.redirect),
        rememberUserName: str(data?.rememberUserName),
        userName: str(data?.userName),
        renderWebView: data?.renderWebView === true,
    };
}
function LoginPage() {
    const { appData } = useAppContext();
    const page = useMemo(() => readPageData(appData.data), [appData.data]);
    const companyKey = appData.companykey;
    const [userName, setUserName] = useState(page.userName);
    const [password, setPassword] = useState('');
    const [clientError, setClientError] = useState('');
    const [missing, setMissing] = useState({ username: false, password: false });
    const [submitting, setSubmitting] = useState(false);
    const [capsLock, setCapsLock] = useState(false);
    const remembered = page.rememberUserName !== '';
    useEffect(() => {
        const onKey = (event) => {
            if (event.getModifierState)
                setCapsLock(event.getModifierState('CapsLock'));
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('keyup', onKey);
        // The MUI page registered these with an empty dep array and never removed
        // them; on a page that only ever unmounts with the document it was invisible,
        // but there is no reason to carry the leak forward.
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('keyup', onKey);
        };
    }, []);
    // Login is a native form POST: LoginController answers with Set-Cookie plus a
    // redirect, and reports failures by bouncing back to /welcome with a flash
    // message. So this only blocks obviously-empty submissions (which would
    // otherwise cost a round trip and come back as the generic wrong-credentials
    // error) and then lets the browser submit.
    const handleSubmit = (event) => {
        if (!remembered) {
            const trimmed = userName.trim();
            let problem = '';
            if (!trimmed && !password)
                problem = 'Please enter your username and password.';
            else if (!trimmed)
                problem = 'Please enter your username.';
            else if (!password)
                problem = 'Please enter your password.';
            if (problem) {
                event.preventDefault();
                setClientError(problem);
                setMissing({ username: !trimmed, password: !password });
                return;
            }
        }
        setClientError('');
        setMissing({ username: false, password: false });
        setSubmitting(true);
    };
    const errorText = clientError || page.errorMessage;
    const supportPhone = appData.supportPhoneNumber || '';
    const banner = errorText ? (React.createElement(Alert, { color: "red", icon: React.createElement(WarningCircle, { size: 18, weight: "fill" }) },
        React.createElement(Text, { size: "sm", className: classes.alertBody }, errorText))) : page.message ? (React.createElement(Alert, { color: "green", icon: React.createElement(CheckCircle, { size: 18, weight: "fill" }) },
        React.createElement(Text, { size: "sm", className: classes.alertBody }, page.message))) : null;
    return (React.createElement(PublicAuthShell, { logoUrl: page.logoUrl, title: "Sign in to your account", subtitle: "Inspection Support Network", showFooter: !page.renderWebView }, page.renderWebView ? (banner) : (React.createElement(React.Fragment, null,
        React.createElement(AuthCard, { flush: true },
            React.createElement(Group, { justify: "space-between", gap: 16, wrap: "wrap", bg: "gray.1", className: classes.keystrip },
                React.createElement(Stack, { gap: 3 },
                    React.createElement(Text, { size: "11px", fw: 600, c: "dimmed", className: classes.keyLabel }, "Company key"),
                    React.createElement(Text, { size: "sm", fw: 600, c: "brand.6", className: classes.keyValue }, companyKey))),
            React.createElement("form", { method: "post", action: `/${companyKey}/login/${remembered ? 'r' : ''}login`, onSubmit: handleSubmit, className: classes.form },
                page.redirect && React.createElement("input", { type: "hidden", name: "redirect", value: page.redirect }),
                React.createElement(Stack, { gap: 18 },
                    banner,
                    remembered ? (React.createElement(React.Fragment, null,
                        React.createElement(TextInput, { label: "Username", placeholder: "Enter username", value: page.rememberUserName, disabled: true }),
                        React.createElement(Button, { component: "a", href: `/${companyKey}/login/notme`, variant: "default", fullWidth: true }, "Log in as a different user"))) : (React.createElement(React.Fragment, null,
                        React.createElement(TextInput, { label: "Username", name: "username", id: "username", autoComplete: "username", placeholder: "Your ISN username", value: userName, onChange: (e) => setUserName(e.currentTarget.value), error: missing.username }),
                        React.createElement(PasswordInput, { label: (React.createElement(Group, { justify: "space-between", align: "baseline", gap: 12, w: "100%" },
                                React.createElement("span", null, "Password"),
                                React.createElement(Anchor, { size: "xs", href: `/${companyKey}/welcome/password` }, "Forgot password?"))), labelProps: { style: { width: '100%' } }, name: "password", id: "password", autoComplete: "current-password", placeholder: "Your password", value: password, onChange: (e) => setPassword(e.currentTarget.value), error: missing.password }),
                        capsLock && (React.createElement(Group, { gap: 6, align: "center" },
                            React.createElement(Info, { size: 14 }),
                            React.createElement(Text, { size: "xs", c: "dimmed" }, "Note: you currently have CAPS LOCK on"))),
                        React.createElement(Group, { justify: "space-between", gap: 12 },
                            React.createElement(Checkbox, { name: "rememberpassword", id: "rememberpassword", label: "Keep me signed in" }),
                            React.createElement(Anchor, { size: "xs", c: "dimmed", href: "https://www.inspectionsupport.com", target: "_blank", rel: "external" }, "Need an account?")))),
                    React.createElement(Button, { type: "submit", fullWidth: true, loading: submitting }, submitting ? 'Logging in' : 'Login'),
                    React.createElement(Text, { size: "xs", c: "dimmed" },
                        "By clicking Login, you agree to our",
                        ' ',
                        React.createElement(Anchor, { size: "xs", href: "https://www.inspectionsupport.com/isn-terms-of-service/", target: "_blank", rel: "external" }, "Terms of Service"),
                        ' ',
                        "and have read and acknowledge our",
                        ' ',
                        React.createElement(Anchor, { size: "xs", href: "https://www.inspectionsupport.com/privacy/", target: "_blank", rel: "external" }, "Privacy Policy"),
                        ". Updated on ",
                        TERMS_UPDATED,
                        ".")))),
        supportPhone && (React.createElement(Text, { size: "xs", c: "dimmed", ta: "center" },
            "Trouble signing in? Call",
            ' ',
            React.createElement(Anchor, { size: "xs", href: `tel:${supportPhone.replace(/[^0-9+]/g, '')}` }, supportPhone),
            "."))))));
}
export default function Login() {
    return (React.createElement(PublicContainer, null,
        React.createElement(LoginPage, null)));
}
const rootEl = document.getElementById('root');
if (rootEl) {
    createRoot(rootEl).render(React.createElement(Login, null));
}
